import React, { useMemo } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { GTFonts, useTheme } from '../../../theme';
import Typography from '../../Reusables/Typography';
import { fs, s, v, formatMoney } from '../../../utils/libs';
import { formatDisplayDateTime } from '../../../utils/formatDisplayDateTime';
import type { OrderDoc } from '../../../redux/backoffice/ordersSlice';

export type BackOfficeOrderRowAmount = 'total' | 'balance';

type Props = {
  order: OrderDoc;
  /** Zero-based list index; rendered as a 1-based row number. */
  index: number;
  /** e.g. "ksh" — already run through replaceCurrencyString. */
  currencyText: string;
  /** `total` shows bp_payment_amount (Completed/Pending tabs); `balance`
   * shows bp_payment_amount_due (Partially Paid tab). */
  amount?: BackOfficeOrderRowAmount;
  onPress: (order: OrderDoc) => void;
};

/**
 * The back office order list row: numbered, "Customer - N Items",
 * Total/Balance, relative date and receipt id. Shared by the Orders tab
 * (Completed / Pending / Partially Paid) and the per-customer orders screen
 * so the two surfaces never drift apart.
 */
const BackOfficeOrderRow: React.FC<Props> = ({
  order,
  index,
  currencyText,
  amount = 'total',
  onPress,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  if (!order) return null;
  const rowNumber = index + 1;
  const payment: any = order?.bp_order_master_payment_information;
  const amountValue =
    amount === 'balance'
      ? payment?.bp_payment_amount_due
      : payment?.bp_payment_amount;
  const customerName = String(order?.bp_order_customer_information || '');
  const itemCount = order.bp_sales_products_information?.length ?? 0;
  return (
    <View style={styles.container}>
      <Pressable
        unstable_pressDelay={80}
        accessibilityLabel={`View Order ${rowNumber}`}
        accessibilityHint="Tap to view order details"
        style={({ pressed, hovered }: any) => [
          styles.section,
          Platform.OS === 'web' && styles.sectionWeb,
          Platform.OS === 'web' && hovered && styles.sectionHovered,
          pressed && { backgroundColor: theme.colors.textTertiary, opacity: 0.2 },
        ]}
        onPress={() => onPress(order)}>
        <View style={styles.rowWithNumber}>
          <View style={styles.rowNumberColumn}>
            <Typography style={styles.rowNumberText}>{rowNumber}</Typography>
          </View>
          <View style={styles.rowContent}>
            <View style={styles.top}>
              <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                <Typography style={styles.name}>
                  {customerName.length > 16
                    ? `${customerName.substring(0, 16)}...`
                    : customerName}
                </Typography>
                <Typography style={[styles.productCount, { marginLeft: s(4) }]}>
                  {'\u2022'}
                  {itemCount}
                  {'\u00A0'}
                  {itemCount > 1 ? 'Items' : 'Item'}
                </Typography>
              </View>
              <Typography style={[styles.text]}>
                {amount === 'balance' ? 'Balance:' : 'Total:'}
                {'\u00A0'}
                <Typography
                  style={[styles.text, { fontFamily: GTFonts.semibold }]}>
                  {currencyText}
                  {'\u00A0'}
                  {formatMoney(amountValue)}
                </Typography>
              </Typography>
            </View>
            <View style={styles.bottom}>
              <Typography style={[styles.text, { fontSize: fs(14) }]}>
                {formatDisplayDateTime(order.bp_order_master_updated_at, '', {
                  relativeDayLabels: true,
                })}
              </Typography>
              <Typography style={styles.text}>
                {order.bp_order_master_receipt_reference_id ? (
                  <>
                    Receipt Id:{'\u00A0'}
                    <Typography
                      style={[styles.text, { fontFamily: GTFonts.semibold }]}>
                      {order.bp_order_master_receipt_reference_id}
                    </Typography>
                  </>
                ) : null}
              </Typography>
            </View>
          </View>
        </View>
      </Pressable>
    </View>
  );
};

export default React.memo(BackOfficeOrderRow);

// Styles are the Orders tab's row styles, moved here unchanged.
const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {},
    rowWithNumber: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    rowNumberColumn: {
      minWidth: s(24),
      marginRight: s(8),
      alignItems: 'center',
      justifyContent: 'center',
    },
    rowNumberText: {
      fontSize: Platform.OS === 'web' ? fs(15) : fs(13),
      lineHeight: Platform.OS === 'web' ? fs(18) : fs(16),
      fontFamily: GTFonts.semibold,
      color: theme.colors.text,
      textAlign: 'center',
    },
    rowContent: {
      flex: 1,
    },
    section: {
      borderColor: theme.colors.shadowLight,
      borderBottomWidth: 1,
      padding: s(5),
      paddingTop: v(5),
    },
    sectionWeb: {
      ...(Platform.OS === 'web'
        ? ({
            cursor: 'pointer',
            transition: 'background-color 0.15s ease-out',
            borderRadius: 4,
            paddingHorizontal: s(8),
            marginHorizontal: s(-8),
          } as any)
        : {}),
    },
    sectionHovered: {
      backgroundColor: 'rgba(0,0,0,0.04)',
    },
    top: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingRight: s(2),
    },
    productCount: {
      fontFamily: GTFonts.regular,
      color: theme.colors.shadow,
      fontSize: fs(14),
      lineHeight: fs(20),
    },
    bottom: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginTop: 10,
      paddingRight: s(2),
    },
    name: {
      marginVertical: v(1),
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(18),
    },
    text: {
      lineHeight: 20,
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      color: theme.colors.graphiteDark,
    },
  });
